import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ParamsValues } from '../models/params-values';
import { DefaultHttpService } from './default-http.service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class ParamsValuesService extends DefaultHttpService {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    /**
     * Récupère les informations des paramètres d'un groupe.
     * @param groupId Id du groupe.
     * @returns Retourne un ParamsValues.
     */
    getForGroup(groupId: string): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http
            .get<ParamsValues>(this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets`, this.getParameterizedHttpOptions([{ key: 'groupId', value: groupId }]))
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Récupère les informations des paramètres d'un groupe.
     * @param groupId Id du groupe.
     * @returns Retourne un ParamsValues.
     */
    getForUser(userId: string): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http
            .get<ParamsValues>(this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets`, this.getParameterizedHttpOptions([{ key: 'userId', value: userId }]))
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Récupère les informations des paramètres hérités par un utilisateur.
     * @param userId Id de l'utilisateur.
     * @returns Retourne un ParamsValues.
     */
    getInheritedByUser(userId: string): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http
            .get<ParamsValues>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets`,
                this.getParameterizedHttpOptions([
                    { key: 'userId', value: userId },
                    { key: 'inheritedOnly', value: 'true' },
                ])
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Récupère les informations des paramètres d'une organisation.
     * @param organizationId Id de l'organisation.
     * @returns Retourne un ParamsValues.
     */
    getForOrganization(organizationId: string): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http.get<ParamsValues>(this.baseUrl + `/api/Organizations/${organizationId}/ParamsSets`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    /**
     * Exporte les informations des paramètres d'un groupe.
     * @param groupId Id du groupe.
     */
    exportForGroup(groupId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http
            .get(this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets/export`, this.getParameterizedHttpOptions([{ key: 'groupId', value: groupId }], 'blob'))
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    /**
     * Exporte les informations des paramètres d'un utilisateur.
     * @param userId Id du groupe.
     */
    exportForUser(userId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http
            .get(this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets/export`, this.getParameterizedHttpOptions([{ key: 'userId', value: userId }], 'blob'))
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    exportForOrganization(organizationId: string): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${organizationId}/ParamsSets/export`, this.getCustomResponseTypeHttpOptions('blob')).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    update(organizationId: string, paramsValuesId: number, paramsValues: ParamsValues): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http.put<ParamsValues>(this.baseUrl + `/api/Organizations/${organizationId}/ParamsSets/${paramsValuesId}`, paramsValues, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    createForUser(userId: string, paramsValues: ParamsValues): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http
            .post<ParamsValues>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets`,
                paramsValues,
                this.getParameterizedHttpOptions([{ key: 'userId', value: userId }])
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    createForGroup(groupId: string, paramsValues: ParamsValues): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http
            .post<ParamsValues>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/ParamsSets`,
                paramsValues,
                this.getParameterizedHttpOptions([{ key: 'groupId', value: groupId }])
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    createForOrganization(organizationId: string, paramsValues: ParamsValues): Observable<ParamsValues> {
        // Appel de la Web Api
        return this.http.post<ParamsValues>(this.baseUrl + `/api/Organizations/${organizationId}/ParamsSets`, paramsValues, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }
}
